import type { SelectProps as AntSelectProps } from 'antd';
import type { FieldNamesType } from 'antd/es/cascader';
import type { DefaultOptionType, BaseOptionType, RefSelectProps } from 'antd/es/select';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { selectArrowIcon } from 'config/theme/components/select';
import { isMessageDescriptor } from 'utils/i18nUtils';

import StyledSelect from './Select.styled';

export type SelectProps = {
   withSearch?: boolean;
    loadMore?: () => void;
    filter?: (input: string, option: DefaultOptionType | undefined) => boolean;
} & AntSelectProps;

const getPopupContainer = (node: HTMLElement): HTMLElement => {
  let area: HTMLElement | null = node;
  let hasModalParent = false;

  while (area && area.nodeName !== 'BODY' && !hasModalParent) {
    hasModalParent = area.classList.contains('ant-modal');
    area = area.parentElement;
  }

  return hasModalParent ? area! : document.body;
};

export const addDataCy = (
  options: (DefaultOptionType | BaseOptionType)[] | undefined,
  fieldNames: FieldNamesType | undefined
): (DefaultOptionType | BaseOptionType)[] | undefined => {
  const valueField = (fieldNames?.value as string) ?? 'value';

  return options?.map(option => ({
    'data-cy': option[valueField],
    ...option,
    ...(option.options ? { options: addDataCy(option.options, fieldNames) } : {}),
  }));
};

const FBSelect = React.forwardRef<RefSelectProps, SelectProps>(
  ({ withSearch, filter, className, loadMore, children, options, placeholder, prefix, onPopupScroll, showSearch, mode, ...rest }, ref) => {
    const intl = useIntl();
    const { fieldNames } = rest;

    const isBlue = className?.split(/\s+/).includes('blue') ?? false;

    const defaultFilter = React.useCallback(
      (input: string, option: DefaultOptionType | undefined): boolean => {
        const { props } = option || {};
        const { children: filterChildren, default: filterDefault } = props || {};

        if (filterDefault) {
          return false;
        }

        const labelField = (fieldNames?.label) ?? 'label';

        if (typeof filterChildren === 'string' || typeof props[labelField] === 'string') {
          const optionLabel: string = props[labelField] ?? filterChildren;
          return optionLabel.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        }

        if (filterChildren?.type === FormattedMessage || isMessageDescriptor(props[labelField]?.props)) {
          const formattedMessageProps = filterChildren?.props ?? props[labelField]?.props;
          const value = intl.formatMessage(formattedMessageProps);
          return value.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        }
        return true;
      },
      [intl, fieldNames]
    );

    const mergedShowSearch = React.useMemo(() => {
      const impliesSearch = mode === 'tags' || (mode === 'multiple' && showSearch === undefined);
      const enabled = showSearch ?? (withSearch || impliesSearch);
      if (!enabled) {
        return showSearch;
      }
      return typeof enabled === 'object'
        ? { searchIcon: selectArrowIcon, ...enabled }
        : { searchIcon: selectArrowIcon };
    }, [showSearch, withSearch, mode]);

    const searchProps = React.useMemo(
      () => (withSearch
        ? {
          optionFilterProp: 'children',
          filterOption: typeof filter === 'function' ? filter : defaultFilter,
        }
        : {}),
      [withSearch, filter, defaultFilter]
    );

    const scrollListener = React.useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, offsetHeight, scrollHeight } = event.target as HTMLDivElement;

        if (typeof loadMore === 'function' && Math.ceil(scrollTop + offsetHeight) >= scrollHeight) {
          loadMore();
        }
      },
      [loadMore]
    );

    const handlePopupScroll = React.useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        onPopupScroll?.(event);
        scrollListener(event);
      },
      [onPopupScroll, scrollListener]
    );

    const formattedOptions = React.useMemo(
      () => addDataCy(
        options,
        fieldNames
      ),
      [options, fieldNames]
    );

    return (
      <StyledSelect
        ref={ref}
        className={className}
        getPopupContainer={getPopupContainer}
        {...rest}
        onPopupScroll={handlePopupScroll}
        prefix={isBlue ? placeholder : prefix}
        placeholder={isBlue ? undefined : placeholder}
        options={formattedOptions}
        {...searchProps}
        mode={mode}
        showSearch={mergedShowSearch}
      >
        {children}
      </StyledSelect>
    );
  }
);

export default FBSelect;
